/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Load scoped Classroom directories, resources, activities, and the full active member roster through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports same module model; app environment; shared demo data; TanStack Query
 * @forbidden routes; app bootstrap; direct generated SDK; Zustand entity copies
 * @public-api via modules/classrooms/index.ts
 * @side-effects authenticated GET requests outside demo mode and local fixture invalidation in local E2E
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/classrooms/api/classrooms.queries.test.tsx and modules/classrooms/components/ClassroomMemberManagement.test.tsx
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { classFileSchema } from '@vispo/contracts';
import { useEffect } from 'react';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';
import {
  type DemoClassroomSummary,
  type DemoRunSummary,
  demoActivities,
  demoClasses,
  demoRuns,
} from '../../../shared/lib/demo-adapters';
import { useActiveContext } from '../../auth';
import type { ClassroomAccessGrant, StudentJoinLinkSummary } from '../model/access-grants';
import {
  type ClassActivitySummary,
  type ClassMemberSummary,
  type ClassResourceSummary,
  type ClassroomSummary,
  mapExistingClassMemberResponse,
  mapExistingClassResponse,
} from '../model/types';
import {
  classroomInvitationsRequest,
  mapAccessGrant,
  mapStudentJoinLinkSummary,
  studentJoinLinksRequest,
} from './access-grants';
import {
  archivedClassroomDirectoryRequest,
  type ClassroomStudentSummaryApiResponse,
  classroomDirectoryRequest,
  classroomMembershipDirectoryRequest,
  classroomMembersRequest,
  classroomStudentMembersRequest,
  classroomStudentSummaryRequest,
} from './classroom-directory';

export const classroomKeys = {
  all: (activeContextId: string) => ['classrooms', activeContextId] as const,
  archived: (activeContextId: string) =>
    [...classroomKeys.all(activeContextId), 'archived'] as const,
  members: (activeContextId: string, classId: string) =>
    [...classroomKeys.all(activeContextId), classId, 'members'] as const,
  studentMembers: (activeContextId: string, classId: string) =>
    [...classroomKeys.all(activeContextId), classId, 'members', 'role=student'] as const,
  studentSummary: (
    activeContextId: string,
    classId: string,
    studentId: string,
    from: string,
    to: string,
  ) =>
    [
      ...classroomKeys.all(activeContextId),
      classId,
      'student-summary',
      studentId,
      from,
      to,
    ] as const,
  resources: (activeContextId: string, classId: string) =>
    [...classroomKeys.all(activeContextId), classId, 'resources'] as const,
  runs: (activeContextId: string, classId: string, role: 'teacher' | 'student') =>
    [...classroomKeys.all(activeContextId), classId, 'activities', role] as const,
  invitations: (activeContextId: string, classId: string, status: string) =>
    [...classroomKeys.all(activeContextId), classId, 'invitations', status] as const,
  studentJoinLinks: (activeContextId: string, classId: string) =>
    [...classroomKeys.all(activeContextId), classId, 'student-join-links'] as const,
};

interface ClassroomAccessGrantE2eFixture {
  grants: ClassroomAccessGrant[];
  status: 'denied' | 'error' | 'loading' | 'ready';
}

interface ClassroomDirectoryE2eFixture {
  classes: DemoClassroomSummary[];
  fetchCount: number;
  retryRequested?: boolean;
  retryStatus?: 'denied' | 'error' | 'loading' | 'ready';
  status: 'denied' | 'error' | 'loading' | 'ready';
}

function classroomDirectoryE2eFixture(
  adapter: { read(name: string): unknown } | undefined,
): ClassroomDirectoryE2eFixture | undefined {
  return adapter?.read('classroomDirectory') as ClassroomDirectoryE2eFixture | undefined;
}

interface ClassroomMembersE2eFixture {
  members: ClassMemberSummary[];
  status: 'conflict' | 'denied' | 'error' | 'loading' | 'pending' | 'ready' | 'stale';
}

function classroomMembersE2eFixture(
  adapter: { read(name: string): unknown } | undefined,
): ClassroomMembersE2eFixture | undefined {
  return adapter?.read('classroomMembers') as ClassroomMembersE2eFixture | undefined;
}

function classroomAccessGrantE2eFixture(
  adapter: { read(name: string): unknown } | undefined,
): ClassroomAccessGrantE2eFixture | undefined {
  return adapter?.read('classroomAccessGrant') as ClassroomAccessGrantE2eFixture | undefined;
}

interface StudentJoinLinksE2eFixture {
  links: StudentJoinLinkSummary[];
  status: 'denied' | 'error' | 'loading' | 'ready';
}

function studentJoinLinksE2eFixture(
  adapter: { read(name: string): unknown } | undefined,
): StudentJoinLinksE2eFixture | undefined {
  return adapter?.read('studentJoinLinks') as StudentJoinLinksE2eFixture | undefined;
}

export function useStudentJoinLinks(activeContextId: string, classroomId: string) {
  const environment = useWorkspaceEnvironment();
  const organizationId = useActiveContext().data?.organizationId;
  return useQuery({
    queryKey: classroomKeys.studentJoinLinks(activeContextId, classroomId),
    enabled: Boolean(classroomId) && (environment.demoMode || Boolean(organizationId)),
    queryFn: async (): Promise<StudentJoinLinkSummary[]> => {
      if (environment.demoMode) {
        const fixture = studentJoinLinksE2eFixture(environment.testAdapter);
        if (!fixture) return [];
        if (fixture.status === 'loading') return new Promise(() => undefined);
        if (fixture.status === 'denied')
          throw Object.assign(new Error('agent-b-cl-f14 denied fixture'), { status: 403 });
        if (fixture.status === 'error') throw new Error('agent-b-cl-f14 join link fixture error');
        return fixture.links.map((link) => ({ ...link }));
      }
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return (
        await studentJoinLinksRequest(environment.apiClient, classroomId, organizationId)
      ).map(mapStudentJoinLinkSummary);
    },
  });
}

export function useClassroomInvitations(
  activeContextId: string,
  classroomId: string,
  status = 'all',
) {
  const environment = useWorkspaceEnvironment();
  const organizationId = useActiveContext().data?.organizationId;
  return useQuery({
    queryKey: classroomKeys.invitations(activeContextId, classroomId, status),
    enabled: Boolean(classroomId) && (environment.demoMode || Boolean(organizationId)),
    queryFn: async (): Promise<ClassroomAccessGrant[]> => {
      if (environment.demoMode) {
        const fixture = classroomAccessGrantE2eFixture(environment.testAdapter);
        if (!fixture) return [];
        if (fixture.status === 'loading') return new Promise(() => undefined);
        if (fixture.status === 'denied')
          throw Object.assign(new Error('agent-b-cl-f03 denied fixture'), { status: 403 });
        if (fixture.status === 'error') throw new Error('agent-b-cl-f03 grant fixture error');
        return fixture.grants.map((grant) => ({ ...grant }));
      }
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return (
        await classroomInvitationsRequest(
          environment.apiClient,
          classroomId,
          organizationId,
          status,
        )
      ).map(mapAccessGrant);
    },
  });
}

interface ClassroomLifecycleE2eFixture {
  archived: ClassroomSummary[];
  status: 'denied' | 'error' | 'loading' | 'ready';
}

interface ClassroomLeaveE2eFixture {
  classrooms: ClassroomSummary[];
  status: 'conflict' | 'denied' | 'error' | 'loading' | 'not-found' | 'pending' | 'ready' | 'stale';
}

function classroomLeaveE2eFixture(
  adapter: { read(name: string): unknown } | undefined,
): ClassroomLeaveE2eFixture | undefined {
  return adapter?.read('classroomLeave') as ClassroomLeaveE2eFixture | undefined;
}

function classroomLifecycleE2eFixture(
  adapter: { read(name: string): unknown } | undefined,
): ClassroomLifecycleE2eFixture | undefined {
  return adapter?.read('classroomLifecycle') as ClassroomLifecycleE2eFixture | undefined;
}

export function useArchivedClassrooms(activeContextId: string, enabled = true) {
  const environment = useWorkspaceEnvironment();
  const activeContextQuery = useActiveContext();
  const organizationId = useActiveOrganizationId() ?? activeContextQuery.data?.organizationId;
  return useQuery({
    queryKey: classroomKeys.archived(activeContextId),
    enabled: enabled && (environment.demoMode || Boolean(organizationId)),
    queryFn: async (): Promise<ClassroomSummary[]> => {
      if (environment.demoMode) {
        const fixture = classroomLifecycleE2eFixture(environment.testAdapter);
        if (!fixture) return [];
        if (fixture.status === 'loading') return new Promise(() => undefined);
        if (fixture.status === 'denied')
          throw Object.assign(new Error('agent-b-cl-f02 denied fixture'), { status: 403 });
        if (fixture.status === 'error') throw new Error('agent-b-cl-f02 archive fixture error');
        return fixture.archived.map((classroom) => ({ ...classroom }));
      }
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      return (await archivedClassroomDirectoryRequest(environment.apiClient, organizationId)).map(
        mapExistingClassResponse,
      );
    },
  });
}

const classworkItemSchema = z
  .object({
    id: z.string().min(1),
    item_type: z.enum(['assignment', 'question', 'material', 'quiz']),
    title: z.string().min(1),
    status: z.enum(['draft', 'published', 'archived']).nullable().optional(),
    due_date: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .loose();

const studentExamSchema = z
  .object({
    assignment_id: z.string().min(1),
    exam_title: z.string().min(1),
    class_id: z.string().nullable().optional(),
    due_date: z.string().nullable().optional(),
    status: z.string(),
    percentage: z.number().nullable().optional(),
    submitted_at: z.string().nullable().optional(),
  })
  .loose();

export function useClassrooms(
  activeContextId: string,
  options: { audience?: 'student' | 'teacher'; enabled?: boolean } = {},
) {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const activeContextQuery = useActiveContext();
  const organizationId = useActiveOrganizationId() ?? activeContextQuery.data?.organizationId;
  const isUnassignedStudent =
    options.audience === 'student' && activeContextId.endsWith(':unassigned:student');
  useEffect(() => {
    if (!environment.demoMode || !environment.testAdapter || options.audience !== 'teacher') return;
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: classroomKeys.all(activeContextId) });
    };
    globalThis.addEventListener('vispo-classroom-directory-fixture-changed', refresh);
    return () =>
      globalThis.removeEventListener('vispo-classroom-directory-fixture-changed', refresh);
  }, [
    activeContextId,
    environment.demoMode,
    environment.testAdapter,
    options.audience,
    queryClient,
  ]);
  return useQuery({
    queryKey: classroomKeys.all(activeContextId),
    enabled: options.enabled !== false && (environment.demoMode || Boolean(organizationId)),
    ...(environment.demoMode && environment.testAdapter ? { retry: false } : {}),
    queryFn: async (): Promise<ClassroomSummary[]> => {
      if (environment.demoMode) {
        const directoryFixture = classroomDirectoryE2eFixture(environment.testAdapter);
        if (directoryFixture) {
          directoryFixture.fetchCount += 1;
          if (directoryFixture.status === 'loading') return new Promise(() => undefined);
          if (directoryFixture.status === 'denied')
            throw Object.assign(new Error('agent-a-sh-f01 directory denied fixture'), {
              status: 403,
            });
          if (directoryFixture.status === 'error') {
            if (!directoryFixture.retryRequested)
              throw new Error('agent-a-sh-f01 directory error fixture');
            directoryFixture.status = directoryFixture.retryStatus ?? 'ready';
          }
          globalThis.dispatchEvent(new Event('vispo-classroom-directory-fetched'));
          return directoryFixture.classes.map((item) => ({
            ...item,
            canEditBasicDetails: true,
            canManageLifecycle: true,
            revision: 1,
            status: 'active' as const,
          }));
        }
        if (options.audience === 'student') {
          const leaveFixture = classroomLeaveE2eFixture(environment.testAdapter);
          if (leaveFixture) {
            if (leaveFixture.status === 'loading') return new Promise(() => undefined);
            if (leaveFixture.status === 'denied')
              throw Object.assign(new Error('agent-b-cl-f13 denied fixture'), { status: 403 });
            if (leaveFixture.status === 'error')
              throw new Error('agent-b-cl-f13 leave fixture error');
            return leaveFixture.classrooms.map((classroom) => ({ ...classroom }));
          }
        }
        return demoClasses.map((item) => ({
          ...item,
          canEditBasicDetails: true,
          canManageLifecycle: true,
          revision: 1,
          status: 'active' as const,
        }));
      }
      if (isUnassignedStudent) return [];
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      const response = await (options.audience === 'student'
        ? classroomMembershipDirectoryRequest(environment.apiClient, organizationId)
        : classroomDirectoryRequest(environment.apiClient, organizationId));
      return response.map(mapExistingClassResponse);
    },
  });
}

export function useClassResources(activeContextId: string, classId: string) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.resources(activeContextId, classId),
    queryFn: async (): Promise<ClassResourceSummary[]> => {
      if (environment.demoMode) {
        return [
          {
            id: `${classId}-resource-1`,
            name: '一元一次方程式課堂講義.pdf',
            kind: 'document',
            mimeType: 'application/pdf',
            sizeBytes: 1_482_000,
            url: null,
            status: 'ready',
          },
          {
            id: `${classId}-resource-2`,
            name: '第 3 章練習題',
            kind: 'exam',
            mimeType: 'application/pdf',
            sizeBytes: 864_000,
            url: null,
            status: 'ready',
          },
        ];
      }
      const files = z
        .array(classFileSchema)
        .parse(
          await environment.apiClient.request<unknown>(
            `/api/classes/${encodeURIComponent(classId)}/resources`,
          ),
        );
      return files.flatMap((file): ClassResourceSummary[] =>
        file.status === 'deleted'
          ? []
          : [
              {
                id: file.id,
                name: file.name,
                kind: file.file_type,
                mimeType: file.mime_type,
                sizeBytes: file.size_bytes,
                url: file.signed_url,
                status: file.status,
              },
            ],
      );
    },
  });
}

export function useClassRuns(
  activeContextId: string,
  classId: string,
  role: 'teacher' | 'student',
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.runs(activeContextId, classId, role),
    queryFn: async (): Promise<ClassActivitySummary[]> => {
      if (environment.demoMode) {
        return demoRuns
          .filter((run: DemoRunSummary) => run.classId === classId)
          .flatMap((run: DemoRunSummary): ClassActivitySummary[] => {
            const activity = demoActivities.find(
              (item: (typeof demoActivities)[number]) => item.id === run.activityId,
            );
            if (!activity) return [];
            return [
              {
                id: activity.id,
                title: activity.title,
                kind: 'quiz',
                status: 'published',
                dueDate: null,
                updatedAt: null,
                progressLabel: run.status === 'live' ? '課堂進行' : '指派作業',
                scoreLabel: run.status === 'live' ? '進行中' : '收件中',
              },
            ];
          });
      }

      if (role === 'student') {
        const rows = z
          .array(studentExamSchema)
          .parse(await environment.apiClient.request<unknown>('/api/students/me/runs'));
        return rows
          .filter((row) => row.class_id === classId)
          .map(
            (row): ClassActivitySummary => ({
              id: row.assignment_id,
              title: row.exam_title,
              kind: 'quiz',
              status: 'published',
              dueDate: row.due_date ?? null,
              updatedAt: row.submitted_at ?? null,
              progressLabel: studentProgressLabel(row.status, row.due_date),
              scoreLabel:
                typeof row.percentage === 'number'
                  ? `${Math.round(row.percentage)}%`
                  : row.status === 'submitted' || row.status === 'graded'
                    ? '已完成'
                    : '待完成',
            }),
          );
      }

      const rows = z
        .array(classworkItemSchema)
        .parse(
          await environment.apiClient.request<unknown>(
            `/api/classes/${encodeURIComponent(classId)}/runs`,
          ),
        );
      return rows.map(
        (row): ClassActivitySummary => ({
          id: row.id,
          title: row.title,
          kind: row.item_type,
          status: row.status ?? 'draft',
          dueDate: row.due_date ?? null,
          updatedAt: row.updated_at ?? null,
          progressLabel:
            row.item_type === 'material'
              ? '課堂教材'
              : row.status === 'published'
                ? '已發布'
                : row.status === 'archived'
                  ? '已封存'
                  : '草稿',
          scoreLabel:
            row.status === 'published' ? '收件中' : row.status === 'archived' ? '已結束' : '草稿',
        }),
      );
    },
  });
}

function studentProgressLabel(status: string, dueDate: string | null | undefined): string {
  if (status === 'submitted' || status === 'graded') return '已完成';
  if (!dueDate) return '待開始';
  const date = new Date(dueDate);
  return Number.isFinite(date.getTime())
    ? `期限 ${new Intl.DateTimeFormat('zh-TW', {
        month: 'numeric',
        day: 'numeric',
      }).format(date)}`
    : '待完成';
}

export function useClassStudents(
  activeContextId: string,
  classItem: Pick<ClassroomSummary, 'id' | 'students'>,
) {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!environment.demoMode) return;
    const refresh = () => {
      const queryKey = classroomKeys.members(activeContextId, classItem.id);
      void queryClient.invalidateQueries({ queryKey });
    };
    globalThis.addEventListener('vispo-classroom-members-fixture-changed', refresh);
    return () => globalThis.removeEventListener('vispo-classroom-members-fixture-changed', refresh);
  }, [activeContextId, classItem.id, environment.demoMode, queryClient]);
  return useQuery({
    queryKey: classroomKeys.members(activeContextId, classItem.id),
    queryFn: async (): Promise<ClassMemberSummary[]> => {
      if (environment.demoMode) {
        const fixture = classroomMembersE2eFixture(environment.testAdapter);
        if (fixture) {
          if (fixture.status === 'loading') return new Promise(() => undefined);
          if (fixture.status === 'denied')
            throw Object.assign(new Error('agent-b-cl-f07 denied fixture'), { status: 403 });
          if (fixture.status === 'error') throw new Error('agent-b-cl-f07 member fixture error');
          if (fixture.status === 'stale')
            throw Object.assign(new Error('agent-b-cl-f07 stale fixture'), { status: 403 });
          return fixture.members.map((member) => ({ ...member }));
        }
        return classItem.students.map((name, index) => ({
          id: `${classItem.id}-member-${index}`,
          userId: `${classItem.id}-student-${index}`,
          name,
          email: null,
          role: 'student',
          status: 'active',
          lastSeenAt: null,
        }));
      }
      const response = await classroomMembersRequest(environment.apiClient, classItem.id);
      return response
        .map(mapExistingClassMemberResponse)
        .filter((member) => member.status === 'active');
    },
  });
}

export function useClassStudentsFiltered(
  activeContextId: string,
  classItem: Pick<ClassroomSummary, 'id'> & { students: readonly string[] },
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.studentMembers(activeContextId, classItem.id),
    queryFn: async (): Promise<ClassMemberSummary[]> => {
      if (environment.demoMode) {
        const fixture = environment.testAdapter?.read('classroomStudentPatrol') as
          | {
              status: 'loading' | 'empty' | 'denied' | 'error' | 'ready';
              students?: ClassMemberSummary[];
            }
          | undefined;
        if (fixture?.status === 'loading') return new Promise(() => undefined);
        if (fixture?.status === 'denied')
          throw Object.assign(new Error('agent-b-cl-f06 denied fixture'), { status: 403 });
        if (fixture?.status === 'error')
          throw Object.assign(new Error('agent-b-cl-f06 roster fixture error'), { status: 503 });
        if (fixture?.status === 'empty') return [];
        if (fixture?.students) return fixture.students.map((student) => ({ ...student }));
        return classItem.students.map((name, index) => ({
          id: `${classItem.id}-student-member-${index}`,
          userId: `${classItem.id}-student-${index}`,
          name,
          email: null,
          role: 'student' as const,
          status: 'active' as const,
          lastSeenAt: null,
        }));
      }
      const response = await classroomStudentMembersRequest(environment.apiClient, classItem.id);
      return response
        .map(mapExistingClassMemberResponse)
        .filter((member) => member.status === 'active');
    },
  });
}

export function trailingThirtyDayPeriod(now = new Date()): { from: string; to: string } {
  const to = new Date(now);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 29);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function useClassroomStudentSummary(
  activeContextId: string,
  classroomId: string,
  studentId: string | null,
) {
  const environment = useWorkspaceEnvironment();
  const period = trailingThirtyDayPeriod();
  return useQuery({
    queryKey: classroomKeys.studentSummary(
      activeContextId,
      classroomId,
      studentId ?? 'none',
      period.from,
      period.to,
    ),
    enabled: Boolean(studentId),
    queryFn: async (): Promise<ClassroomStudentSummaryApiResponse> => {
      if (!studentId) throw new Error('student id is required');
      if (environment.demoMode) {
        const fixture = environment.testAdapter?.read('classroomStudentSummary') as
          | {
              status: 'loading' | 'empty' | 'denied' | 'not-found' | '422' | 'error';
              records?: ClassroomStudentSummaryApiResponse['records'];
            }
          | undefined;
        if (fixture?.status === 'loading') return new Promise(() => undefined);
        if (fixture?.status === 'denied')
          throw Object.assign(new Error('agent-b-cl-f06 summary denied fixture'), { status: 403 });
        if (fixture?.status === 'not-found')
          throw Object.assign(new Error('agent-b-cl-f06 summary not-found fixture'), {
            status: 404,
          });
        if (fixture?.status === 'error')
          throw Object.assign(new Error('agent-b-cl-f06 summary fixture error'), { status: 503 });
        if (fixture?.status === '422')
          throw Object.assign(new Error('agent-b-cl-f06 summary invalid period fixture'), {
            status: 422,
          });
        if (fixture?.status === 'empty')
          return { classroom_id: classroomId, student_id: studentId, ...period, records: [] };
        return {
          classroom_id: classroomId,
          student_id: studentId,
          ...period,
          records: fixture?.records ?? [],
        };
      }
      return classroomStudentSummaryRequest(
        environment.apiClient,
        classroomId,
        studentId,
        period.from,
        period.to,
      );
    },
  });
}
